import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Col, Form, Input, Row, Spin } from 'antd';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import dayjs, { Dayjs } from 'dayjs';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import { ReactComponent as QuestionCircleIcon } from 'images/question-round.svg';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import AlertMessage, { AlertInfo } from 'components/AlertMessage/AlertMessage';
import BoxPopover from 'components/BoxPopover/BoxPopover';
import DatePickerInput from 'components/DatePickerInput/DatePickerInput';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import InfoBanner from 'components/InfoBanner/InfoBanner';
import StyledCheckbox from 'components/StyledCheckbox/StyledCheckbox';
import InputForm from 'components/InputForm/InputForm';
import BenefitGuide from 'model/BenefitGuide';
import {
  ALL_BENEFIT_CATEGORIES,
  BENGUIDE_NAME_DUPLICATE_MESSAGE,
  EMPTY_MESSAGE,
} from 'constants/commonConstants';
import RouteMap from 'routes/RouteMap';
import MultiSelectDropdown, {
  Option,
} from 'components/MultiSelectDropdown/MultiSelectDropdown';
import {
  INPUT_DATE_FORMAT_DEFAULT,
  MMDDYYYY_SLASHES_FORMAT,
} from 'utils/dateUtil';
import {
  classOptionsFor,
  coversDate,
  coveringPlanYearNames,
} from 'modules/effectiveDate/utils/guideScope';
import {
  findRemovedPlans,
  RemovedPlans,
} from 'modules/effectiveDate/utils/dateChange';
import { getPlansForDate } from 'modules/effectiveDate/services/EffectiveDateService';
import ChangeEffectiveDateDialog from 'modules/home/components/ChangeEffectiveDateDialog/ChangeEffectiveDateDialog';
import BenefitGuideBasicInfo, {
  buildBenefitGuideBasicInfo,
} from 'model/BenefitGuideBasicInfo ';
import {
  defaultRouteTitles,
  RouteKeyConstants,
} from 'constants/routeKeyConstants';
import {
  ADDITIONAL_CONTENT_OPTIONS,
  BasicInfoAdditionalContent,
  BASIC_INFO_ERROR_MESSAGES,
  BASIC_INFO_EXIT_MESSAGES,
  BenefitSection,
  BENEFIT_TYPES,
  BENGUIDE_CREATE_ERRORS,
  EFFECTIVE_DATE_NO_PLAN_YEAR_MESSAGE,
  DUPLICATE_BENGUIDE_NAME_ERROR_CODE,
  NHC_REGENERATE_WARNING_MSG,
  PLAN_LOOKUP_FAILED_ERROR_CODE,
  PLAN_LOOKUP_FAILED_MESSAGE,
  REMOVE_BENEFIT_MODAL_MESSAGE,
  REMOVE_CONTENT_MODAL_MESSAGE,
  UPDATE_NHC_CANCEL_TEXT,
  UPDATE_NHC_CONFIRMATION_TEXT,
  UPDATE_NHC_MODAL_DESCRIPTION,
} from 'modules/home/constants';
import {
  CLOSE_CREATE_BENGUIDE_MODAL,
  CREATE_BENGUIDE_CHANNEL,
  EDIT_BENGUIDE_CHANNEL,
  EDIT_BENGUIDE_STARTED,
} from 'modules/clients/UPClient/UPClientConst';
import {
  createBenguide,
  clearSaveError,
  findEmployerById,
  getPlanYears,
  resetBenefitGuides,
  updateBasicInfo,
  findBenguideByHash,
  saveBenGuideFailed,
  saveBenGuideStarted,
  updateBasicInfoLocal,
  updateBasicInfoWithoutCheckListData,
} from 'modules/home/slices/basicInfoSlice';

import {
  getContentTypeFromValue,
  getPlansForBenefit,
  getBenefitFromValue,
  hasCustomContent,
  isBenefitClassChangeValid,
} from 'modules/home/util';
import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import useBenguide from 'hooks/benguide';

import CustomTitlePopover from 'modules/home/components/CustomTitlePopover/CustomTitlePopover';

import styles from './basicInfo.module.less';

const GUIDE_NAME_FIELD = 'guideName';
const EFFECTIVE_DATE_FIELD = 'effectiveDate';
const VOLUNTARY_BENEFITS_LABEL = 'Voluntary Benefits';

type SaveRecovery = {
  employerId: string;
  effectiveDate: string;
  scope: string;
};

const BasicInfo: FC = () => {
  const [benefitClassOptions, setBenefitGroupOptions] = useState<Option[]>([]);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertInfo>({
    type: 'error',
    message: '',
  });
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] =
    useState<boolean>(false);
  const [updatedBenGuide, setUpdatedBenGuide] = useState<BenefitGuideBasicInfo>(
    {} as BenefitGuideBasicInfo
  );
  const [valueToDisable, setValueToDisable] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [selectedBenefits, setSelectedBenefits] = useState<string[]>(
    Object.values(BENEFIT_TYPES).map((benefit) => benefit.value)
  );
  const [selectedContent, setSelectedContent] = useState<string[]>(
    Object.values(ADDITIONAL_CONTENT_OPTIONS).map((content) => content.value)
  );
  const [plansExist, setPlansExist] = useState<boolean>(false);
  const [dateChange, setDateChange] = useState<{
    benGuide: BenefitGuideBasicInfo;
    removed: RemovedPlans[];
  } | null>(null);
  const [saveRecovery, setSaveRecovery] = useState<SaveRecovery | null>(null);
  const [isRetryingSave, setIsRetryingSave] = useState(false);
  const [isReviewingDateChange, setIsReviewingDateChange] = useState(false);
  const saveAttemptRef = useRef<SaveRecovery | null>(null);
  const recoveryVersionRef = useRef(0);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const [shownDate, setShownDate] = useState<string | undefined>();
  const [coveringYearNames, setCoveringYearNames] = useState<string[]>([]);

  const [params] = useSearchParams();

  const location = useLocation();

  const { latestRevision } = useBenguide();

  const isEdit = useMemo(() => {
    const isCreateMatch = matchPath(
      location.pathname,
      RouteMap.CREATE_BENGUIDE
    );
    return Boolean(!isCreateMatch);
  }, [location]);

  const {
    inProgress,
    requestType,
    benefitGuide: benguideData,
    error: apiError,
    basicInfo,
  } = useAppSelector((state) => state.basicInfo);
  const benguideId = useAppSelector((state) => state.basicInfo.masterId);
  const { urlHash, revision } = useBenguide();
  const employer = useAppSelector((state) => state.basicInfo.employer);
  const planYears = useAppSelector((state) => state.basicInfo.planYears);
  const { savedGuide } = useAppSelector((state) => state.basicInfo);
  const benGuide = useAppSelector((state) => state.benguide.data);
  const state = useAppSelector((state) => state);
  const {
    benguide: { customNavigationTitles },
  } = state;
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [form] = Form.useForm();

  const upClient = useUPClient();

  const employerId: string | null = useMemo(() => {
    if (isEdit) {
      return benguideData ? benguideData.employerId : '';
    }
    return params.get('employerId');
  }, [isEdit, benguideData, params]);

  // The plan years' own dates, the rule the server refuses a date by. The
  // plans-for-date answer cannot stand in: it leaves out a year with no plans.
  const noPlanYearCovers =
    shownDate !== undefined &&
    !planYears.some((planYear) => coversDate(planYear, shownDate));

  const toEffectiveDate = (value?: Dayjs | null) =>
    value ? value.format(INPUT_DATE_FORMAT_DEFAULT) : undefined;

  useEffect(() => {
    setCoveringYearNames([]);
    if (!employerId || !shownDate) {
      return undefined;
    }
    let stale = false;
    (async () => {
      try {
        const { data } = await getPlansForDate(employerId, shownDate);
        if (!stale) {
          setCoveringYearNames(coveringPlanYearNames(data));
        }
      } catch (error) {
        // Only a hint; a failed read must not get in the way of the form.
      }
    })();
    return () => {
      stale = true;
    };
  }, [employerId, shownDate]);

  const saveScope = useCallback(
    (benGuide: BenefitGuideBasicInfo) =>
      JSON.stringify({
        employerId: benGuide.employerId,
        masterId: benGuide.masterId ?? null,
        name: benGuide.name,
        effectiveDate: benGuide.effectiveDate ?? null,
        benefitClasses: [...(benGuide.benefitClasses ?? [])].sort(),
        benefits: [...selectedBenefits].sort(),
        content: [...selectedContent].sort(),
      }),
    [selectedBenefits, selectedContent]
  );

  const currentSaveScope = useCallback(() => {
    if (!employerId) {
      return undefined;
    }
    const { guideName, planYear, benefitClasses, effectiveDate } =
      form.getFieldsValue();
    const benGuide = buildBenefitGuideBasicInfo(
      guideName,
      planYear,
      benefitClasses,
      employerId,
      selectedBenefits,
      selectedContent,
      toEffectiveDate(effectiveDate)
    );
    if (benguideData) {
      benGuide.masterId = benguideData.masterId;
    }
    return saveScope(benGuide);
  }, [
    benguideData,
    employerId,
    form,
    saveScope,
    selectedBenefits,
    selectedContent,
  ]);

  const recordSaveAttempt = (benGuide: BenefitGuideBasicInfo) => {
    if (benGuide.effectiveDate) {
      saveAttemptRef.current = {
        employerId: benGuide.employerId,
        effectiveDate: benGuide.effectiveDate,
        scope: saveScope(benGuide),
      };
    } else {
      saveAttemptRef.current = null;
    }
  };

  const activateSaveRecovery = (benGuide: BenefitGuideBasicInfo) => {
    recordSaveAttempt(benGuide);
    recoveryVersionRef.current += 1;
    setSaveRecovery(saveAttemptRef.current);
    setIsReviewingDateChange(false);
    setAlertMessage({
      type: 'error',
      message: PLAN_LOOKUP_FAILED_MESSAGE,
    });
    setIsAlertVisible(true);
  };

  const invalidateSaveRecovery = useCallback(() => {
    recoveryVersionRef.current += 1;
    setSaveRecovery(null);
    setIsRetryingSave(false);
    setIsReviewingDateChange(false);
    setDateChange(null);
    if (apiError?.code === PLAN_LOOKUP_FAILED_ERROR_CODE) {
      dispatch(clearSaveError());
    }
  }, [apiError?.code, dispatch]);

  const retrySaveLookup = async () => {
    if (!saveRecovery) {
      return;
    }
    const retryVersion = recoveryVersionRef.current + 1;
    recoveryVersionRef.current = retryVersion;
    setIsRetryingSave(true);
    try {
      await getPlansForDate(
        saveRecovery.employerId,
        saveRecovery.effectiveDate
      );
      if (
        recoveryVersionRef.current !== retryVersion ||
        currentSaveScope() !== saveRecovery.scope
      ) {
        return;
      }
      dispatch(clearSaveError());
      setSaveRecovery(null);
      setAlertMessage({
        type: 'success',
        message:
          'Plans are available again. Review your information, then select Save & Next.',
      });
      setIsAlertVisible(true);
    } catch (error) {
      if (
        recoveryVersionRef.current === retryVersion &&
        currentSaveScope() === saveRecovery.scope
      ) {
        setAlertMessage({
          type: 'error',
          message: PLAN_LOOKUP_FAILED_MESSAGE,
        });
        setIsAlertVisible(true);
      }
    } finally {
      if (recoveryVersionRef.current === retryVersion) {
        setIsRetryingSave(false);
      }
    }
  };

  // The server stores this on each save; there is no live preview of a pending change.
  const unmatchedClassNote = useMemo(() => {
    const unmatched = benguideData?.unmatchedBenefitClasses ?? [];
    if (unmatched.length === 0) {
      return undefined;
    }
    return (
      <div data-cy="unmatchedBenefitClasses">
        {unmatched.map(({ className, planYearId }) => {
          const name =
            planYears.find((planYear) => planYear.id === planYearId)?.name ??
            planYearId;
          return (
            <div key={`${className}-${planYearId}`}>
              {`"${className}" is not a class in plan year ${name}. Plans from ${name} will not show for that class.`}
            </div>
          );
        })}
      </div>
    );
  }, [benguideData?.unmatchedBenefitClasses, planYears]);

  const isValueToDisableBenefit = useMemo(() => {
    return Boolean(valueToDisable && getBenefitFromValue(valueToDisable.value));
  }, [valueToDisable]);

  const updateClassOptionsForDate = useCallback(
    (date?: string, keep: string[] = []) => {
      const names = date
        ? classOptionsFor(planYears, benguideData?.planYearIds ?? [], date)
        : [];
      // The field only shows selected values that are options, so a class the
      // broker chose stays listed even when the new date does not offer it.
      const listed = [
        ...names,
        ...keep.filter((name) => !names.includes(name)),
      ];
      setBenefitGroupOptions(
        listed.map((name) => ({ value: name, label: name }))
      );
      return names;
    },
    [planYears, benguideData]
  );

  const isDiffSelectedBenefits = useCallback(() => {
    if (benguideData) {
      const benefits = Object.keys(
        Object.fromEntries(
          Object.entries(benguideData).filter(
            ([key, value]: any[]) =>
              ALL_BENEFIT_CATEGORIES.includes(key) && value.enabled
          )
        )
      );

      return !isEqual(benefits.sort(), selectedBenefits.sort());
    }
  }, [benguideData, selectedBenefits]);

  useEffect(() => {
    if (isEdit) {
      upClient.postMessage({
        channel: EDIT_BENGUIDE_CHANNEL,
        event: EDIT_BENGUIDE_STARTED,
        data: benguideId,
      });
    }
  }, [isEdit, benguideId, upClient]);

  useEffect(() => {
    if (urlHash) {
      dispatch(findBenguideByHash(urlHash, revision));
    }
  }, [dispatch, urlHash, revision]);

  useEffect(() => {
    if (employerId) {
      dispatch(findEmployerById(employerId));
      dispatch(getPlanYears(employerId));
    }
  }, [dispatch, employerId]);

  useEffect(() => {
    if (!isEdit && employer?.name) {
      form.setFieldsValue({
        guideName: `${employer.name} Microsite`,
      });
    }
  }, [employer, isEdit, form]);

  useEffect(() => {
    if (benguideData) {
      form.setFieldsValue({
        guideName: benguideData.name,
        [EFFECTIVE_DATE_FIELD]: benguideData.effectiveDate
          ? dayjs(benguideData.effectiveDate)
          : undefined,
        benefitClasses: benguideData.benefitClasses,
      });
      updateClassOptionsForDate(benguideData.effectiveDate);
      setShownDate(benguideData.effectiveDate);
    }
  }, [benguideData, form, updateClassOptionsForDate]);

  useEffect(() => {
    if (savedGuide) {
      navigate(
        `/${savedGuide.urlHash}/${savedGuide.employerName}?revision=${latestRevision}`
      );
    }
  }, [savedGuide, navigate, dispatch, latestRevision]);

  useEffect(() => {
    if (apiError && requestType === saveBenGuideFailed.type) {
      if (apiError.code) {
        const errorMessage = BENGUIDE_CREATE_ERRORS.find(
          (error) => error.code === apiError.code
        );

        apiError.code === DUPLICATE_BENGUIDE_NAME_ERROR_CODE &&
          form.setFields([
            {
              name: GUIDE_NAME_FIELD,
              errors: [BENGUIDE_NAME_DUPLICATE_MESSAGE],
            },
          ]);

        errorMessage &&
          setAlertMessage({ type: 'error', message: errorMessage.message });
        if (apiError.code === PLAN_LOOKUP_FAILED_ERROR_CODE) {
          const attempt = saveAttemptRef.current;
          if (attempt && currentSaveScope() === attempt.scope) {
            recoveryVersionRef.current += 1;
            setSaveRecovery(attempt);
          }
        }
        setIsAlertVisible(true);
        return;
      }
      setAlertMessage({
        type: 'error',
        message: BASIC_INFO_ERROR_MESSAGES.alertMessage.apiError,
      });
      setIsAlertVisible(true);
    }
  }, [apiError, currentSaveScope, requestType, form]);

  const getEnabledBenefits = (benguideData: BenefitGuide) => {
    return Object.keys(BENEFIT_TYPES).filter((key) => {
      return (benguideData as any)[key] && (benguideData as any)[key].enabled;
    });
  };

  const getEnabledcontents = (benguideData: BenefitGuide) => {
    return Object.keys(ADDITIONAL_CONTENT_OPTIONS).filter((key) => {
      return (benguideData as any)[key] && (benguideData as any)[key].enabled;
    });
  };

  // loads data in edit mode
  useEffect(() => {
    if (benguideData) {
      const enabledBenefits = getEnabledBenefits(benguideData);
      const enabledContent = getEnabledcontents(benguideData);

      setSelectedContent([...enabledContent]);
      setSelectedBenefits([...enabledBenefits]);
    }
  }, [benguideData]);

  useEffect(() => {
    return () => {
      recoveryVersionRef.current += 1;
      dispatch(resetBenefitGuides());
    };
  }, [dispatch]);

  useEffect(() => {
    const { guideName, planYear, benefitClasses, effectiveDate } =
      form.getFieldsValue();
    if (employerId && benguideData && guideName) {
      const enabledBenefits = getEnabledBenefits(benguideData);
      const enabledContent = getEnabledcontents(benguideData);

      const benGuide: BenefitGuideBasicInfo = buildBenefitGuideBasicInfo(
        guideName,
        planYear,
        benefitClasses,
        employerId,
        enabledBenefits,
        enabledContent,
        toEffectiveDate(effectiveDate)
      );

      if (benguideData) {
        benGuide.masterId = benguideData.masterId;
      }

      dispatch(updateBasicInfoLocal(benGuide));
    }
  }, [benguideData, dispatch, employerId, form]);

  useEffect(() => {
    setPlansExist(false);
    Object.values(BenefitSection).filter((section) => {
      const plans = get(state, `${section}.${section}Section.plans`, []).length;
      if (plans > 0) {
        setPlansExist(true);
      }
    });
  }, [state]);

  const handleValuesChange = async (changedValues: any, allValues: any) => {
    invalidateSaveRecovery();
    const { benefitClasses } = changedValues || {};

    if (EFFECTIVE_DATE_FIELD in (changedValues || {})) {
      // On a saved guide the broker's classes are never swapped by a date
      // change: the server reports the ones the new date does not offer
      // (unmatchedBenefitClasses). A new guide has nothing saved to protect and
      // no note to show, so the classes the date does not offer are dropped
      // where the broker can see it.
      const newDate = toEffectiveDate(changedValues[EFFECTIVE_DATE_FIELD]);
      setShownDate(newDate);
      const chosen: string[] = allValues.benefitClasses ?? [];
      const offered = updateClassOptionsForDate(
        newDate,
        benguideData ? chosen : []
      );
      let next = benguideData
        ? chosen
        : chosen.filter((name) => offered.includes(name));
      // With nothing chosen there is nothing to overwrite, so a single offered
      // class is picked for the broker.
      if (chosen.length === 0 && offered.length === 1) {
        next = offered;
      }
      if (next.length !== chosen.length) {
        allValues = { ...allValues, benefitClasses: next };
        form.setFieldsValue({ benefitClasses: next });
      }
    }
    try {
      if (isAlertVisible) {
        await form.validateFields();
        if (isEmpty(benefitClasses)) {
          setIsAlertVisible(false);
        }
      }
    } catch (error: any) {
      const { errorFields = [] } = error;
      if (isEmpty(errorFields)) {
        if (isEmpty(benefitClasses)) {
          setIsAlertVisible(false);
        }
      }
    }

    const {
      guideName,
      planYear: currentPlanYear,
      benefitClasses: benClasses,
      effectiveDate: currentDate,
    } = allValues || {};
    if (employerId) {
      const benGuide: BenefitGuideBasicInfo = buildBenefitGuideBasicInfo(
        guideName,
        currentPlanYear,
        benClasses,
        employerId,
        selectedBenefits,
        selectedContent,
        toEffectiveDate(currentDate)
      );

      if (benguideData) {
        benGuide.masterId = benguideData.masterId;
      }

      dispatch(updateBasicInfoLocal(benGuide));
    }
  };

  const handleSubmitFailed = () => {
    setIsAlertVisible(true);
    setAlertMessage({
      type: 'error',
      message: BASIC_INFO_ERROR_MESSAGES.alertMessage.formError,
    });
  };

  const handleSubmit = (values: any) => {
    if (selectedBenefits.length === 0) {
      setAlertMessage({
        type: 'error',
        message: BASIC_INFO_ERROR_MESSAGES.alertMessage.formError,
      });
      setIsAlertVisible(true);
      return;
    }
    // The class field has no required rule on such a date, so stop here; the
    // date field already says why.
    if (noPlanYearCovers) {
      return;
    }
    const { guideName, planYear, benefitClasses, effectiveDate } = values || {};
    if (employerId) {
      const benGuide: BenefitGuideBasicInfo = buildBenefitGuideBasicInfo(
        guideName,
        planYear,
        benefitClasses,
        employerId,
        selectedBenefits,
        selectedContent,
        toEffectiveDate(effectiveDate)
      );

      if (benguideData) {
        benGuide.masterId = benguideData.masterId;

        if (
          plansExist &&
          benGuide.effectiveDate &&
          benGuide.effectiveDate !== benguideData.effectiveDate
        ) {
          reviewDateChange(benGuide);
        } else {
          saveUpdate(benGuide);
        }
      } else {
        recordSaveAttempt(benGuide);
        dispatch(createBenguide(benGuide));
      }
    }
  };

  const saveUpdate = (benGuide: BenefitGuideBasicInfo) => {
    if (
      benguideData &&
      benguideData.datesAndEligibility.enabled !== benGuide.datesAndEligibility
    ) {
      setUpdatedBenGuide(benGuide);
      setIsRegenerateModalOpen(true);
    } else {
      if (
        !(
          isDiffSelectedBenefits() &&
          selectedContent.includes('newHireCheckList')
        )
      ) {
        recordSaveAttempt(benGuide);
        dispatch(
          updateBasicInfoWithoutCheckListData(
            benGuide,
            selectedContent,
            revision
          )
        );
      } else {
        setUpdatedBenGuide(benGuide);
        setIsRegenerateModalOpen(true);
      }
    }
  };

  // The server removes the plans the new date does not cover; the broker is
  // asked first, and only when something would actually go.
  const reviewDateChange = async (benGuide: BenefitGuideBasicInfo) => {
    const reviewVersion = recoveryVersionRef.current + 1;
    recoveryVersionRef.current = reviewVersion;
    const reviewScope = saveScope(benGuide);
    setIsReviewingDateChange(true);
    try {
      const { data } = await getPlansForDate(
        benGuide.employerId,
        benGuide.effectiveDate as string
      );
      if (
        recoveryVersionRef.current !== reviewVersion ||
        currentSaveScope() !== reviewScope
      ) {
        return;
      }
      const removed = findRemovedPlans(
        benguideData as BenefitGuide,
        data,
        selectedBenefits
      );
      if (removed.length > 0) {
        setIsReviewingDateChange(false);
        setDateChange({ benGuide, removed });
        return;
      }
    } catch (error) {
      // Saving without the preview would remove plans without asking.
      if (
        recoveryVersionRef.current === reviewVersion &&
        currentSaveScope() === reviewScope
      ) {
        activateSaveRecovery(benGuide);
      }
      return;
    }
    setIsReviewingDateChange(false);
    saveUpdate(benGuide);
  };

  const handleConfirmModalCancel = () => {
    if (isReviewingDateChange) {
      recoveryVersionRef.current += 1;
      setIsReviewingDateChange(false);
    }
    const benguide: BenefitGuideBasicInfo = buildBenefitGuideBasicInfo(
      benGuide && benGuide.name,
      benGuide && benGuide.planYearId,
      benGuide && benGuide.benefitClasses,
      benGuide && benGuide.employerId,
      selectedBenefits,
      selectedContent,
      benGuide?.effectiveDate
    );
    benguide.masterId = benGuide && benGuide.masterId;

    if (!isEmpty(basicInfo) && !isEqual(basicInfo, benguide)) {
      setIsExitModalOpen(true);
    } else {
      handleCloseBasicInfo();
    }
  };

  const handleCloseBasicInfo = () => {
    recoveryVersionRef.current += 1;
    form.resetFields();
    setIsExitModalOpen(false);
    if (isEdit && benguideData) {
      navigate(
        `/${benguideData.urlHash}/${benguideData.employerName}?revision=${latestRevision}`
      );
    } else {
      upClient.postMessage({
        channel: CREATE_BENGUIDE_CHANNEL,
        event: CLOSE_CREATE_BENGUIDE_MODAL,
        data: '',
      });
    }
  };

  const handleBenefitChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    if (isEdit && benguideData) {
      const benefitIndex = selectedBenefits.findIndex(
        (benefit) => benefit === value
      );

      const benefitPlans = getPlansForBenefit(value, benguideData);

      if (!isEmpty(benefitPlans) && benefitIndex >= 0 && !checked) {
        setValueToDisable(getBenefitFromValue(value));
        setIsConfirmModalOpen(true);
        return;
      }
    }
    if (checked) {
      invalidateSaveRecovery();
      setIsAlertVisible(false);
      setSelectedBenefits([...selectedBenefits, value]);
      dispatch(updateBasicInfoLocal(benGuide));
      return;
    }
    onConfirmDisable(getBenefitFromValue(value));
    dispatch(updateBasicInfoLocal(benGuide));
  };

  const handleSelectedContentChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    if (isEdit && benguideData) {
      const hasCustom = hasCustomContent(value, benguideData);
      if (!checked && hasCustom) {
        setValueToDisable(getContentTypeFromValue(value));
        setIsConfirmModalOpen(true);
        return;
      }
    }

    invalidateSaveRecovery();
    setIsAlertVisible(false);
    checked
      ? setSelectedContent([...selectedContent, value])
      : setSelectedContent(
          selectedContent.filter((selection) => selection !== value)
        );
    dispatch(updateBasicInfoLocal(benGuide));
  };

  const onConfirmDisable = (
    section: { value: string; label: string } | null
  ) => {
    if (section) {
      invalidateSaveRecovery();
      setIsAlertVisible(false);
    }
    if (section && getBenefitFromValue(section.value)) {
      const newSelections = selectedBenefits.filter(
        (benefit) => benefit !== section.value
      );
      setSelectedBenefits(newSelections);
      dispatch(updateBasicInfoLocal(benGuide));
    }
    if (section && getContentTypeFromValue(section.value)) {
      const updatedContentSelection = selectedContent.filter(
        (content) => content !== section.value
      );
      setSelectedContent(updatedContentSelection);
      dispatch(updateBasicInfoLocal(benGuide));
    }
    setValueToDisable(null);
    setIsConfirmModalOpen(false);
  };

  const cancelRegenerateNHC = () => {
    recordSaveAttempt(updatedBenGuide);
    dispatch(
      updateBasicInfo(updatedBenGuide, selectedContent, false, revision)
    );
    setIsRegenerateModalOpen(false);
  };

  const regenerateNHC = () => {
    recordSaveAttempt(updatedBenGuide);
    dispatch(updateBasicInfo(updatedBenGuide, selectedContent, true, revision));
  };

  const isCreatedBeforeRelease = (createdTs?: string | null): boolean => {
    if (!createdTs) return false;

    const creationDate = new Date(createdTs);
    // Prod release date (September 15, 2025)
    const releaseDate = new Date(2025, 8, 15);

    creationDate.setHours(0, 0, 0, 0);
    releaseDate.setHours(0, 0, 0, 0);

    // Return true if creation date is before release date
    return creationDate < releaseDate;
  };

  const showRenamePopover = useCallback(
    (customTitleKey: string) => {
      const customTitle =
        customNavigationTitles?.data?.[
          customTitleKey as keyof typeof RouteKeyConstants
        ];

      const createdTs =
        benguideData?.initialCreatedTs || benGuide?.initialCreatedTs;

      // Use conditional logic based on isCreatedBeforeRelease only for VOLUNTARY_BENEFITS and WORKSITE
      let defaultTitle = defaultRouteTitles?.[customTitleKey];

      if (
        (customTitleKey === RouteKeyConstants.VOLUNTARY_BENEFITS ||
          customTitleKey === RouteKeyConstants.WORKSITE) &&
        isCreatedBeforeRelease(createdTs)
      ) {
        defaultTitle = VOLUNTARY_BENEFITS_LABEL;
      }

      return customTitle && customTitle !== defaultTitle ? (
        <CustomTitlePopover customTitle={customTitle} />
      ) : (
        ''
      );
    },
    [
      customNavigationTitles.data,
      benguideData?.initialCreatedTs,
      benGuide?.initialCreatedTs,
    ]
  );

  return (
    <div className={styles.container}>
      {(inProgress && requestType !== saveBenGuideStarted.type) ||
      isEmpty(planYears) ? (
        <Spin />
      ) : (
        <div className={styles.content}>
          <BenefitGuideRefreshChannel />
          <InfoBanner
            header="Basic Information &amp; Content"
            description="We'll walk you through everything you need to create a great microsite. To start off, review the information below and click next when you're ready to continue"
            className={styles.infoBanner}
          />
          <div className={styles.formContainer}>
            {isAlertVisible && (
              <AlertMessage
                className={styles.alert}
                type={alertMessage.type}
                message={
                  saveRecovery ? (
                    <>
                      {alertMessage.message}
                      <Button
                        type="link"
                        onClick={retrySaveLookup}
                        loading={isRetryingSave}
                      >
                        Retry
                      </Button>
                    </>
                  ) : (
                    alertMessage.message
                  )
                }
                closeAlert={() => setIsAlertVisible(false)}
                closeTextStatus={!saveRecovery}
                timed={!saveRecovery}
              />
            )}

            <div className={styles.sectionHeader}>Basic Info</div>
            <InputForm
              labelCol={{ span: 8 }}
              labelAlign="left"
              form={form}
              onValuesChange={handleValuesChange}
              onFinishFailed={handleSubmitFailed}
              onFinish={handleSubmit}
            >
              <Row gutter={10}>
                <Col span={23}>
                  <Form.Item
                    className={styles.guideName}
                    label="Microsite name"
                    name={GUIDE_NAME_FIELD}
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: EMPTY_MESSAGE,
                      },
                    ]}
                  >
                    <Input data-cy="guideName" />
                  </Form.Item>
                </Col>
                <Col span={1}>
                  <BoxPopover
                    header="Name of Microsite"
                    description="This is the name of the microsite that other admins will see. No employees will be able to see this name. You may want to include additional identifying terms such as the plan year, benefit class, or geographic location that this microsite is for."
                  >
                    <QuestionCircleIcon className={styles.popoverIcon} />
                  </BoxPopover>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col span={23}>
                  <Form.Item
                    name={EFFECTIVE_DATE_FIELD}
                    label="What is the effective date of this microsite?"
                    validateStatus={noPlanYearCovers ? 'error' : undefined}
                    help={
                      noPlanYearCovers
                        ? EFFECTIVE_DATE_NO_PLAN_YEAR_MESSAGE
                        : undefined
                    }
                    extra={
                      coveringYearNames.length > 0 && (
                        <div data-cy="coveringPlanYears">
                          {`Plan years on this date: ${coveringYearNames.join(
                            ', '
                          )}`}
                        </div>
                      )
                    }
                    rules={[{ required: true, message: EMPTY_MESSAGE }]}
                  >
                    <DatePickerInput
                      data-cy="effectiveDate"
                      format={MMDDYYYY_SLASHES_FORMAT}
                      placeholder={MMDDYYYY_SLASHES_FORMAT}
                      allowClear={false}
                    />
                  </Form.Item>
                </Col>
                <Col span={1}>
                  <BoxPopover
                    header="Effective Date and Microsites"
                    description="Select the date this microsite takes effect. You will only be able to add plans to this microsite that are active on that date."
                  >
                    <QuestionCircleIcon className={styles.popoverIcon} />
                  </BoxPopover>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col span={23}>
                  <Form.Item
                    label="What benefit classes is this microsite for?"
                    name="benefitClasses"
                    extra={unmatchedClassNote}
                    rules={[
                      {
                        // The date field says why the list is empty.
                        required: !noPlanYearCovers,
                        message: EMPTY_MESSAGE,
                      },
                      ({ getFieldValue }) => ({
                        validator: async (rule, value) => {
                          if (
                            !isEmpty(value) &&
                            toEffectiveDate(
                              getFieldValue(EFFECTIVE_DATE_FIELD)
                            ) === benguideData?.effectiveDate &&
                            isBenefitClassChangeValid(
                              value,
                              isEdit,
                              benguideData,
                              plansExist
                            )
                          ) {
                            setAlertMessage({
                              type: 'warning',
                              message:
                                BASIC_INFO_ERROR_MESSAGES.alertMessage
                                  .effectiveDate,
                            });
                            setIsAlertVisible(true);
                            return Promise.reject(
                              new Error(
                                BASIC_INFO_ERROR_MESSAGES.benefitClasses.fieldChange
                              )
                            );
                          }
                        },
                        warningOnly: true,
                      }),
                    ]}
                  >
                    <MultiSelectDropdown
                      dataCy="benefitClasses"
                      options={benefitClassOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={1}>
                  <BoxPopover
                    header="Benefit Classes and Microsites"
                    description="Select which Benefit Class(es) this microsite is for. You will only be able to add plans to this microsite that are available to at least one of the benefit classes selected. If different classes have different employee contributions for certain plans, this will indicate which contributions the microsite will display."
                  >
                    <QuestionCircleIcon className={styles.popoverIcon} />
                  </BoxPopover>
                </Col>
              </Row>
              <div className={styles.section}>
                <div>
                  <Form.Item
                    wrapperCol={{ span: 24 }}
                    className={styles.benefitTypeItem}
                    labelCol={{ span: 24 }}
                    label={
                      <div className={styles.sectionHeader}>
                        What Benefits Should Be In This Microsite?
                        <BoxPopover
                          header="Included Benefits"
                          description="Select the benefits that should be included in this microsite. You can change these at any time."
                          trigger="hover"
                        >
                          <QuestionCircleIcon className={styles.popoverIcon} />
                        </BoxPopover>
                      </div>
                    }
                    validateStatus={
                      selectedBenefits.length === 0 ? 'error' : 'validating'
                    }
                    help={
                      selectedBenefits.length === 0
                        ? BASIC_INFO_ERROR_MESSAGES.benefitTypes.empty
                        : ''
                    }
                    messageVariables={{ error: 'Error ' }}
                  >
                    <Checkbox.Group
                      data-cy="includedBenefits"
                      value={selectedBenefits}
                    >
                      <Row>
                        {Object.values(BENEFIT_TYPES).map(
                          ({ value, label, customTitleKey }, index) => {
                            // Get createdTs from benefit guide data (DBG)
                            const createdTs =
                              benguideData?.initialCreatedTs ||
                              benGuide?.initialCreatedTs;

                            // Apply conditional logic for label display
                            let displayLabel = label;
                            if (
                              (customTitleKey ===
                                RouteKeyConstants.VOLUNTARY_BENEFITS ||
                                customTitleKey ===
                                  RouteKeyConstants.WORKSITE) &&
                              isCreatedBeforeRelease(createdTs)
                            ) {
                              displayLabel = VOLUNTARY_BENEFITS_LABEL;
                            }

                            return (
                              <Col span={6} key={index}>
                                <StyledCheckbox
                                  value={value}
                                  onChange={handleBenefitChange}
                                >
                                  {displayLabel}
                                </StyledCheckbox>
                                {showRenamePopover(customTitleKey)}
                              </Col>
                            );
                          }
                        )}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </div>
              </div>
              <div className={styles.sectionHeader}>
                What Additional Content Should Be In This Microsite?
                {/* TODO: set link to sample guide */}
                <BoxPopover
                  header="Additional Content"
                  description={
                    <div className={styles.popoverContent}>
                      <div>
                        This additional content provides employees with key
                        benefits education as well as assistance in navigating
                        their benefits options. Anything that you select now can
                        be changed at any time.
                      </div>
                    </div>
                  }
                >
                  <QuestionCircleIcon className={styles.popoverIcon} />
                </BoxPopover>
              </div>
              <div>
                <Form.Item wrapperCol={{ span: 24 }}>
                  <Checkbox.Group
                    data-cy="additionalContent"
                    className={styles.formSection}
                    value={selectedContent}
                  >
                    <Row>
                      {Object.keys(ADDITIONAL_CONTENT_OPTIONS).map(
                        (key, index) => {
                          const { value, label, customTitleKey } =
                            ADDITIONAL_CONTENT_OPTIONS[
                              key as BasicInfoAdditionalContent
                            ];
                          const totalItems = Object.keys(
                            ADDITIONAL_CONTENT_OPTIONS
                          ).length;
                          return (
                            <Col
                              style={{ width: `${100 / totalItems}%` }}
                              key={index}
                            >
                              <StyledCheckbox
                                value={value}
                                onChange={handleSelectedContentChange}
                              >
                                {label}
                              </StyledCheckbox>
                              {showRenamePopover(customTitleKey)}
                            </Col>
                          );
                        }
                      )}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </div>
              <div className={styles.formActions}>
                <Button
                  type="default"
                  size="large"
                  data-cy="BasicInfoDBGCancel"
                  onClick={handleConfirmModalCancel}
                  disabled={
                    inProgress && requestType === saveBenGuideStarted.type
                  }
                >
                  Cancel
                </Button>
                <Button
                  ref={saveButtonRef}
                  type="primary"
                  size="middle"
                  className={styles.saveBtn}
                  htmlType="submit"
                  data-cy="BasicInfoDBGSubmit"
                  loading={
                    inProgress && requestType === saveBenGuideStarted.type
                  }
                  disabled={
                    Boolean(saveRecovery) ||
                    isRetryingSave ||
                    isReviewingDateChange
                  }
                >
                  Save &amp; Next
                </Button>
              </div>
            </InputForm>
          </div>
          <ConfirmationDialog
            visible={isExitModalOpen}
            flipFooterOrder={!isEdit}
            onCancel={() => setIsExitModalOpen(false)}
            onOk={handleCloseBasicInfo}
            title={
              isEdit
                ? BASIC_INFO_EXIT_MESSAGES.editPage.title
                : BASIC_INFO_EXIT_MESSAGES.createPage.title
            }
            okText={
              isEdit
                ? BASIC_INFO_EXIT_MESSAGES.editPage.confirmText
                : BASIC_INFO_EXIT_MESSAGES.createPage.confirmText
            }
            cancelText={
              isEdit
                ? BASIC_INFO_EXIT_MESSAGES.editPage.cancelText
                : BASIC_INFO_EXIT_MESSAGES.createPage.cancelText
            }
          >
            <div>
              {isEdit
                ? BASIC_INFO_EXIT_MESSAGES.editPage.message
                : BASIC_INFO_EXIT_MESSAGES.createPage.message}
            </div>
          </ConfirmationDialog>
          <ConfirmationDialog
            visible={isConfirmModalOpen}
            onCancel={() => {
              setIsConfirmModalOpen(false);
              setValueToDisable(null);
            }}
            cancelText="Cancel"
            flipFooterOrder
            okText={
              valueToDisable
                ? `Yes - Remove ${valueToDisable.label}`
                : 'Yes - Remove Section'
            }
            onOk={() => onConfirmDisable(valueToDisable)}
            title={valueToDisable ? `Remove ${valueToDisable.label}` : 'Remove'}
          >
            {isValueToDisableBenefit
              ? REMOVE_BENEFIT_MODAL_MESSAGE
              : REMOVE_CONTENT_MODAL_MESSAGE}
          </ConfirmationDialog>

          {dateChange && (
            <ChangeEffectiveDateDialog
              visible
              date={dateChange.benGuide.effectiveDate as string}
              removed={dateChange.removed}
              onCancel={() => {
                setDateChange(null);
                // The dialog is unmounted, not hidden, so the modal's own
                // focus restore never runs and focus would fall to <body>.
                saveButtonRef.current?.focus();
              }}
              onOk={() => {
                const { benGuide } = dateChange;
                setDateChange(null);
                saveUpdate(benGuide);
              }}
            />
          )}

          <ConfirmationDialog
            visible={isRegenerateModalOpen}
            onCancel={() => {
              cancelRegenerateNHC();
            }}
            cancelText={UPDATE_NHC_CANCEL_TEXT}
            flipFooterOrder
            okText={UPDATE_NHC_CONFIRMATION_TEXT}
            onOk={() => regenerateNHC()}
            title="Update New Hire Checklist?"
          >
            <>
              <p className={styles.nhcUpdateText}>
                {UPDATE_NHC_MODAL_DESCRIPTION}
              </p>
              <br />
              {NHC_REGENERATE_WARNING_MSG}
            </>
          </ConfirmationDialog>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;
